import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSessionStore } from '../hooks/useSession';
import { ConsentModal } from './ConsentModal';
import { LovensePanel } from './LovensePanel';
import { SafeWordButton } from './SafeWordButton';
import { ConsentAgreement, GameType } from '../types';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

export const SessionLobby = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const role = (searchParams.get('role') || 'B') as 'A' | 'B';

  const { session, loadSession, updateSession, setConsent } = useSessionStore();

  const [showConsent, setShowConsent] = useState(false);
  const [partnerConnected, setPartnerConnected] = useState(false);
  const [selectedGame, setSelectedGame] = useState<GameType>('rhythm');

  useEffect(() => {
    if (!sessionId) return;
    const userId = 'user-' + Date.now();

    loadSession(sessionId).then(() => {
      const { joinSession } = useSessionStore.getState();
      joinSession(sessionId, userId, role).then((ok) => {
        if (ok) {
          toast.success('Joined session!');
        } else {
          toast.error('Session not found');
        }
      });
    });
  }, [sessionId]);

  useEffect(() => {
    if (!session) return;

    const partner = role === 'A' ? session.partnerB : session.partnerA;
    setPartnerConnected(partner.connected);

    if (!session.consent.established && partner.connected) {
      setShowConsent(true);
    }
  }, [session, role]);

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session-${sessionId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sessions',
        filter: `id=eq.${sessionId}`
      }, (payload) => {
        if (payload.eventType === 'UPDATE' && payload.new) {
          const updated = payload.new as any;
          const mapped = {
            id: updated.id,
            createdBy: updated.created_by,
            partnerA: updated.partner_a,
            partnerB: updated.partner_b,
            status: updated.status,
            consent: updated.consent,
            gameState: updated.game_state,
            createdAt: updated.created_at,
            endedAt: updated.ended_at
          };
          updateSession(mapped);

          const myRole = role;
          const partner = myRole === 'A' ? updated.partner_b : updated.partner_a;
          setPartnerConnected(partner?.connected || false);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, role]);

  const handleConsentSubmit = (consent: ConsentAgreement) => {
    setConsent(consent);
    setShowConsent(false);
  };

  const startGame = () => {
    if (!session?.consent.established) {
      toast.error('Please establish consent first');
      return;
    }
    if (!partnerConnected) {
      toast.error('Wait for your partner to join');
      return;
    }
    navigate(`/game/${selectedGame}/${sessionId}?role=${role}`);
  };

  const games: { id: GameType; name: string; description: string }[] = [
    { id: 'rhythm', name: 'Rhythm Match', description: 'Hit the beat in sync with your partner' },
    { id: 'tease-denial', name: 'Tease & Denial', description: "Control each other's pleasure" },
    { id: 'sync-challenge', name: 'Sync Challenge', description: 'Test your connection timing' },
    { id: 'truth-dare', name: 'Truth or Dare', description: 'Intimate questions and challenges' },
    { id: 'breathing', name: 'Breathing', description: 'Synchronized breathing exercise' }
  ];

  return (
    <div>
      <ConsentModal
        isOpen={showConsent}
        onSubmit={handleConsentSubmit}
        onClose={() => {}}
      />

      <div className="card" style={{ marginBottom: '2rem' }}>
        <h2 className="card-title">Session Lobby</h2>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Session Code</p>
            <p style={{ fontSize: '1.5rem', fontFamily: 'monospace', letterSpacing: '0.1em' }}>
              {sessionId}
            </p>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Your Role</p>
            <p style={{ fontSize: '1.25rem' }}>Partner {role}</p>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Partner Status</p>
            <span className={`status-indicator ${partnerConnected ? 'connected' : 'disconnected'}`}>
              <span className={`status-dot ${partnerConnected ? 'connected' : ''}`} />
              {partnerConnected ? 'Connected' : 'Waiting...'}
            </span>
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Consent</p>
            <span className={`status-indicator ${session?.consent.established ? 'connected' : ''}`}>
              <span className={`status-dot ${session?.consent.established ? 'connected' : ''}`} />
              {session?.consent.established ? 'Established' : 'Required'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        <div>
          <h3 style={{ marginBottom: '1rem' }}>Choose a Game</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {games.map((game) => (
              <div
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                style={{
                  padding: '1.5rem',
                  borderRadius: 'var(--border-radius)',
                  background: selectedGame === game.id ? 'var(--primary)' : 'var(--bg-secondary)',
                  border: `2px solid ${selectedGame === game.id ? 'var(--primary)' : 'var(--bg-tertiary)'}`,
                  cursor: 'pointer',
                  transition: 'var(--transition)'
                }}
              >
                <h4 style={{ marginBottom: '0.5rem' }}>{game.name}</h4>
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>{game.description}</p>
              </div>
            ))}
          </div>

          <button
            className="btn btn-primary btn-lg"
            onClick={startGame}
            disabled={!partnerConnected || !session?.consent.established}
            style={{ width: '100%', marginTop: '2rem' }}
          >
            Start {games.find(g => g.id === selectedGame)?.name}
          </button>
        </div>

        <div>
          <LovensePanel />
        </div>
      </div>

      {session?.consent.established && <SafeWordButton sessionId={sessionId!} />}
    </div>
  );
};
